import { Grid, LicensePlate, DatePicker, MileageField, VinField, Alert } from "@tm/components"
import { useCountryCodeToLicensePlate, useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { LicensePlateComponentInitiator, Vehicle } from "@tm/models"
import { ButtonKeyDefinition, isValidDate, ValidationError } from "@tm/utils"
import { useCallback, useEffect, useState } from "react"
import { FullWidthTextField } from "../../../StyledComponents"
import { useVehicleOVerviewStore } from "../../store"

export function VehicleOverview() {
    const { translateText, language } = useLocalization()
    const { workTask, attachToWorkTask } = useWorkTask() ?? {}
    const { vehicle } = workTask || {}
    const { plateCode } = useCountryCodeToLicensePlate(vehicle?.countryCode)

    const [vehicleLoading, setVehicleLoading] = useState<boolean>(false)
    const [validationError, setValidationError] = useState<ValidationError>()
    const [description, setDescription] = useState(
        vehicle ? `${vehicle.manufacturer ?? ""} ${vehicle.modelSeries ?? ""} ${vehicle.model ?? ""}` : undefined
    )
    const [plateId, setPlateId] = useState(vehicle?.plateId)
    const [initialRegistration, setInitialRegistration] = useState(vehicle?.initialRegistration)
    const [mileage, setMileage] = useState(vehicle?.mileAge)
    const [vin, setVin] = useState(vehicle?.vin)

    const { setVehicleData, vehicleData } = useVehicleOVerviewStore((state) => state)

    useEffect(() => {
        setVehicleLoading(false)
    }, [vehicle])

    useEffect(() => {
        setVehicleData({ ...vehicleData, description })
    }, [description])

    const saveVehicle = useCallback(
        async (vehicleToSave: Vehicle) => {
            setVehicleLoading(true)
            setValidationError(undefined)

            await attachToWorkTask?.({ vehicle: vehicleToSave }).catch((error: unknown) => {
                setVehicleLoading(false)

                if (error instanceof ValidationError) {
                    setValidationError(error)
                }
            })
        },
        [attachToWorkTask]
    )

    const handlePlateIdChange = useCallback(
        (newPlateId: string) => {
            setPlateId(newPlateId)
            if (vehicle && newPlateId !== plateId) {
                saveVehicle({ ...vehicle, plateId: newPlateId })
            }
            setVehicleData({ ...vehicleData, plateId: newPlateId })
        },
        [vehicle, plateId, setVehicleData, vehicleData, saveVehicle]
    )

    const handleMilageChange = useCallback(() => {
        setVehicleData({ ...vehicleData, mileAge: mileage })
        if (vehicle && mileage !== vehicle.mileAge) {
            saveVehicle({ ...vehicle, mileAge: mileage })
        }
    }, [setVehicleData, vehicleData, mileage, vehicle, saveVehicle])

    const handleVinChange = useCallback(() => {
        setVehicleData({ ...vehicleData, vin })
        if (vehicle && vin !== vehicle.vin) {
            saveVehicle({ ...vehicle, vin })
        }
    }, [setVehicleData, vehicleData, vin, vehicle, saveVehicle])

    function onAcceptInitialRegistrationDate(acceptedInitialRegistration: Date | null | undefined) {
        setInitialRegistration(acceptedInitialRegistration ?? undefined)
        if (vehicle && acceptedInitialRegistration?.getTime() !== vehicle.initialRegistration?.getTime()) {
            saveVehicle({ ...vehicle, initialRegistration: acceptedInitialRegistration ?? undefined })
        }
    }

    function handleInitialRegistrationDateBlur() {
        if (isValidDate(initialRegistration) || initialRegistration === undefined) {
            onAcceptInitialRegistrationDate(initialRegistration)
        }
    }

    function handleInitialRegistrationDateChangeKeyUp(e: React.KeyboardEvent<HTMLDivElement>) {
        if (
            (e.key === ButtonKeyDefinition.Enter || e.key === ButtonKeyDefinition.Tab) &&
            (isValidDate(initialRegistration) || initialRegistration === undefined)
        ) {
            onAcceptInitialRegistrationDate(initialRegistration)
        }
    }

    return (
        <>
            {validationError && <Alert title={validationError.message} severity="error" />}
            <Grid container spacing={0.5} columns={9} flex={1} flexWrap="wrap" alignItems="end">
                <Grid item sm={4.5}>
                    <FullWidthTextField
                        label={translateText(25)}
                        value={description}
                        onChange={(value) => setDescription(value.target.value)}
                        inputProps={{ maxLength: 200 }}
                    />
                </Grid>
                <Grid item sm={1}>
                    <LicensePlate
                        countryCode={plateCode}
                        size="extralarge"
                        licensePlateComponentInitiator={LicensePlateComponentInitiator.VehicleInfoCrmMicro}
                        style={{ height: "100%" }}
                        isReadOnly={vehicleLoading}
                        disabled={vehicleLoading}
                        value={plateId}
                        onChangeConfirm={handlePlateIdChange}
                    />
                </Grid>
                <Grid item sm={1}>
                    <DatePicker
                        textfieldProps={{
                            sx: { width: "100%" },
                            onBlur: handleInitialRegistrationDateBlur,
                            onKeyUp: (e) => handleInitialRegistrationDateChangeKeyUp(e),
                        }}
                        localisation={{ locale: language, region: null }}
                        size="extralarge"
                        label={translateText(124)}
                        maxDate={new Date()}
                        disabled={vehicleLoading}
                        value={initialRegistration ?? null}
                        onChange={(e) => setInitialRegistration(e ?? undefined)}
                        onAccept={(e) => onAcceptInitialRegistrationDate(e)}
                    />
                </Grid>
                <Grid item sm={1}>
                    <MileageField
                        label={translateText(125)}
                        disabled={vehicleLoading}
                        size="extralarge"
                        value={mileage}
                        onChange={setMileage}
                        onBlur={handleMilageChange}
                    />
                </Grid>
                <Grid item sm={1.5}>
                    <VinField
                        label={translateText(101)}
                        variant="filled"
                        size="extralarge"
                        disabled={vehicleLoading}
                        value={vin}
                        onChange={setVin}
                        onBlur={handleVinChange}
                    />
                </Grid>
            </Grid>
        </>
    )
}
